import {bindable, BindingEngine, Container, customElement, inject, ViewCompiler, ViewSlot} from 'aurelia-framework';
import * as _ from 'lodash';
import {UnsavedChangesChecker} from "../../form/unsaved-changes-checker";
import {TabProcessor} from "./tab-processor";
import {SelectedTabStorage} from "./selected-tab-storage.js";

import {UnsavedChanges} from "../../form/unsaved-changes-prevent";
import {UserClient} from "../../api/user-client";

@inject(
    TabProcessor,
    ViewSlot,
    Container,
    ViewCompiler,
    UnsavedChangesChecker,
    SelectedTabStorage,
    UserClient,
    BindingEngine,
)
@customElement('sio-new-tabs')
export class NewTabs {

    @bindable
    config;

    @bindable
    object;

    _presets = {
        contingentCalendar: [{
            id: 'contingent',
            heading: 'price.models.contingent-calendar.display.tabs.calendar.heading',
            content: '<sio-contingent-view></sio-contingent-view>'
        }, {
            id: 'table',
            heading: 'price.models.contingent-calendar.display.tabs.table.heading',
            content: '<sio-table config=\"price/contingent-calendar\"></sio-table>'
        }],
        tourGuideCalendar: [{
            id: 'contingent',
            heading: 'tour-guide-tourism.tabs.calendar.heading',
            content: '<sio-availability-scheduler  post-url.bind="\'tour-guide-tourism/guide-calendar\'" form-id.bind="\'tour-guide-tourism/guide-calendar-request\'" create-form-id.bind="\'tour-guide-tourism/manage-guide-availability\'" reference-model-id.bind="\'tour-guide-tourism/guide\'" detail-page-id.bind="\'tour-guide-tourism/guides-availability-detail\'"></sio-availability-scheduler>'
        }, {
            id: 'table',
            heading: 'tour-guide-tourism.tabs.table.heading',
            content: '<sio-table config.one-time="\'tour-guide-tourism/guide-availability\'"></sio-table>'
        }],
        salesPartnerCalendar: [{
            id: 'contingent',
            heading: 'sales-partner-availability.availabilityCalendar',
            content: '<sio-availability-scheduler post-url.bind="\'humboldt/sales-partner-calendar\'" form-id.bind="\'humboldt/sales-partner-calendar-request\'" create-form-id.bind="\'humboldt/manage-sales-partner-availability\'" reference-model-id.bind="\'commission-receiver/commission-receiver\'" detail-page-id.bind="\'humboldt/sales-partner-availability-detail\'"></sio-availability-scheduler>'
        }, {
            id: 'table',
            heading: 'tour-guide-tourism.tabs.table.heading',
            content: '<sio-table config.one-time="\'humboldt/sales-partner-availability\'"></sio-table>'
        }]
    };

    selectedTab;
    _tabs = [];

    showWithoutPermission = false;

    constructor(
        tabProcessor,
        viewSlot,
        container,
        viewCompiler,
        unsavedChangesChecker,
        selectedTabStorage,
        userClient,
        bindingEngine,
    ) {
        this.tabProcessor = tabProcessor;
        this.viewSlot = viewSlot;
        this.container = container;
        this.viewCompiler = viewCompiler;
        this.unsavedChangesChecker = unsavedChangesChecker;
        this.selectedTabStorage = selectedTabStorage;
        this.bindingEngine = bindingEngine;
        this.userClient = userClient;

        this.showWithoutPermission = this.userClient.showWithoutPermission;

        // Observe changes in contentLocale
        this.subscription = this.bindingEngine.propertyObserver(this.userClient, 'showWithoutPermission')
            .subscribe((showWithoutPermission) => {
                this.showWithoutPermission = showWithoutPermission;

                this._update();
            });
    }

    async bind(context, overrideContext) {
        this.context = context;
        this.context.object = this.object;
        this.overrideContext = overrideContext;

        this._update();
    }

    dispose() {
        if (this.subscription) {
            this.subscription.dispose();
        }
    }

    async configChanged() {
        this._update();
    }

    async objectChanged() {
        this.context.object = this.object;
        this._update();
    }

    _update() {

        this._tabs = [];

        this._tabs = this.tabProcessor.processTabs(
            this.config && this._presets?.[this.config] ? this._presets[this.config] : null,
            this.object, this.showWithoutPermission
        );

        let oldTab = this.selectedTab != null ? this._tabs.find(tab => tab.id === this.selectedTab) : null;

        const tab = this.selectedTabStorage.getSelectedTab(this._tabs);
        let forceUpdate = false;

        if (oldTab) {

            //Only if panels have changed, we make force update
            if (JSON.stringify(_.omit(oldTab, ['viewFactory', 'content'])) !== JSON.stringify(_.omit(tab, ['viewFactory', 'content']))) {
                forceUpdate = true;
            }
        }

        if (!oldTab && this.selectedTab) {
            this.selectedTab = null;
        }

        if (!tab) {
            let activeTab = null;

            for (let i = 0; i < this._tabs.length; i++) {
                if (this._tabs[i].active && activeTab == null) {
                    activeTab = this._tabs[i].id;
                    break;
                }
            }

            if (this._tabs.length > 0 && !this.selectedTab) {
                this.originalUnsavedChanges = _.clone(UnsavedChanges.changes);
                this.selectTab(activeTab ? activeTab : this._tabs[0].id, forceUpdate);
            }
        } else {
            let activeTab = null;

            for (let i = 0; i < this._tabs.length; i++) {
                if (this._tabs[i] === tab) {
                    activeTab = tab.id;
                    break;
                }
            }

            if (this._tabs.length > 0 && !this.selectedTab) {
                this.originalUnsavedChanges = _.clone(UnsavedChanges.changes);
                this.selectTab(activeTab ? activeTab : this._tabs[0].id, forceUpdate);
            }
        }
    }

    selectTab(key, force) {

        if (key === this.selectedTab && !force) {
            return;
        }

        if (!this.unsavedChangesChecker.canDeactivate(this.originalUnsavedChanges)) {
            return;
        }

        this.unsavedChangesChecker.revertChanges(this.originalUnsavedChanges);
        this.originalUnsavedChanges = _.clone(UnsavedChanges.changes);

        //Remove old view
        if (this.view) {

            this.view.unbind();
            this.viewSlot.remove(this.view, true, true);
        }

        let tab = this._tabs.find(tab => tab.id === key);

        if (this.selectedTab != null) {
            this.selectedTabStorage.selectTab(tab);
        }

        //Add new view
        this.context.tab = tab;
        this.selectedTab = tab.id;

        if (!tab.viewFactory) {
            tab.viewFactory = this.viewCompiler.compile('<template>' + tab.content + '</template>');
        }

        this.view = tab.viewFactory.create(this.container);
        this.view.bind(this.context, this.overrideContext);

        this.viewSlot.add(this.view);
    }
}
